import { locale, t } from '../i18n/index.ts';

let currency = 'ALL';
export const setCurrency = (c: string) => {
  currency = c;
};

/** Minor units → "1,250 L" for lek, "€12.50" for others. */
export function money(minor: number, opts: { sign?: boolean } = {}): string {
  const value = minor / 100;
  const sign = opts.sign && minor > 0 ? '+' : '';
  if (currency === 'ALL') {
    const n = new Intl.NumberFormat(locale(), {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `${sign}${n} L`;
  }
  return sign + new Intl.NumberFormat(locale(), { style: 'currency', currency }).format(value);
}

/** The business's own currency, e.g. "ALL". */
export const baseCurrency = () => currency;

/** Minor units in a given currency: "1,250 L", "€12.50", "CHF 8.00". */
export function moneyIn(minor: number, code: string | null | undefined, opts: { sign?: boolean } = {}): string {
  if (!code || code === currency) return money(minor, opts);
  const value = minor / 100;
  const sign = opts.sign && minor > 0 ? '+' : '';
  if (code === 'ALL') return `${sign}${new Intl.NumberFormat(locale(), { minimumFractionDigits: Number.isInteger(value) ? 0 : 2, maximumFractionDigits: 2 }).format(value)} L`;
  try {
    return sign + new Intl.NumberFormat(locale(), { style: 'currency', currency: code }).format(value);
  } catch {
    return `${sign}${code} ${value.toFixed(2)}`;
  }
}

/** A rate kept in millionths, as people read it: 97850000 → "97.85". */
export function rateText(millionths: number): string {
  return new Intl.NumberFormat(locale(), { maximumFractionDigits: 6 }).format(millionths / 1_000_000);
}

/** What people typed as a rate ("97,85" or "97.85"), or null. */
export function parseRate(text: string): number | null {
  const clean = text.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,6})?$/.test(clean)) return null;
  const n = Number(clean);
  return n > 0 ? n : null;
}

/**
 * Parses what a person typed ("1 200", "12.5", "12,5", "1,250.50") into minor units, or null.
 * The last dot or comma followed by one or two digits is the decimal mark; any other dot,
 * comma or space only groups digits, so it reads the same in English and in Albanian.
 */
export function parseMoney(text: string): number | null {
  let clean = text.replace(/[\s\u00a0\u202f]/g, '').replace(/[^\d.,]/g, '');
  const dec = /[.,](\d{1,2})$/.exec(clean);
  clean = dec ? `${clean.slice(0, dec.index).replace(/[.,]/g, '')}.${dec[1]}` : clean.replace(/[.,]/g, '');
  if (!clean || !/^\d+(\.\d{0,2})?$/.test(clean)) return null;
  return Math.round(Number.parseFloat(clean) * 100);
}

export function qty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, '');
}

/** A big count, short: 1.2K, 3.4M. */
export function compact(n: number): string {
  return new Intl.NumberFormat(locale(), { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

/** A month as "Nov 2026" from "2026-11". */
export function monthName(ym: string): string {
  return new Intl.DateTimeFormat(locale(), { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${ym}-01T00:00:00Z`));
}

/** "now", "4 min", "1 h 12 min" since a moment. */
export function elapsed(since: string | Date, now = Date.now()): string {
  const mins = Math.max(0, Math.floor((now - new Date(since).getTime()) / 60000));
  if (mins < 1) return t('time.now');
  if (mins < 60) return t('time.minutes', { n: mins });
  return t('time.hours', { h: Math.floor(mins / 60), m: mins % 60 });
}

/** `elapsed` within the last day, the date after that: "12 min", "3 h 5 min", "04 Oct 2026". */
export function since(at: string | Date, now = Date.now()): string {
  if (minutesSince(at, now) < 24 * 60) return elapsed(at, now);
  return new Intl.DateTimeFormat(locale(), { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(at));
}

export function minutesSince(since: string | Date, now = Date.now()): number {
  return Math.max(0, Math.floor((now - new Date(since).getTime()) / 60000));
}

export function clock(at: string | Date): string {
  return new Intl.DateTimeFormat(locale(), { hour: '2-digit', minute: '2-digit' }).format(new Date(at));
}

/** "24 Oct 2026, 14:05": unambiguous in every country. */
export function dateTime(at: string | Date): string {
  return new Intl.DateTimeFormat(locale(), { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(at));
}

/** A date without a time, "2026-10-24" → "24 Oct 2026", never shifted by the time zone. */
export function day(d: string): string {
  return new Intl.DateTimeFormat(locale(), { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${d.slice(0, 10)}T00:00:00Z`));
}

/** The day a moment falls on in the browser's time zone, e.g. a sale at 00:18 belongs to that morning. */
export function localDay(at: string | Date): string {
  return day(isoDay(new Date(at)));
}

/** Today's date in the browser's time zone, as "YYYY-MM-DD". */
export function todayIso(): string {
  return isoDay(new Date());
}

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
